import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toast } from "sonner";
import { Plus, Trash2, X } from "lucide-react";

import { DEFAULT_POLICY, buildRateIndex, flatRoles, resolveRate, type RatecardRole } from "@/calc";

import { useEstimates } from "@/braining/store";
import { BlockDraftCard } from "@/braining/components/block-draft-card";
import { NewMark } from "@/braining/components/new-mark";
import { placeInStage, stageIndexOf, type BlocksLibrary } from "@/braining/mocks";
import { counted, int, parseInt10, pct, rub } from "@/braining/format";
import { UNITS } from "@/braining/units";
import { cn } from "@/lib/utils";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

/** Блок справочника и его строка: те же типы, что в документе библиотеки. */
type LibraryBlock = BlocksLibrary["blocks"][number];
type LibraryLine = LibraryBlock["lines"][number];

export const Route = createFileRoute("/library")({ component: LibraryPage });

/**
 * Библиотека блоков работ — то, что у агентства играет роль справочника услуг.
 *
 * Блок это не услуга с формулой, а «Статья + Цель/Подход/Результат + типовой
 * набор ролей с часами». Именно это переиспользуется между проектами.
 */
function LibraryPage() {
  const { ratecard, library, updateLibrary } = useEstimates();
  const { user } = Route.useRouteContext();
  // Предложения считаются по всем проверенным сметам, а видит их тот, кто
  // отвечает за справочник: у менеджера в выборке только его калькуляторы, и
  // «правило агентства» из них не выводится.
  const canLearn = user?.role === "admin" || user?.role === "rop";
  // Тип по умолчанию берём из живого справочника: он приезжает из базы, и на
  // первом рендере там ещё зерно — состояние подхватит настоящий, когда придёт.
  const [type, setType] = useState<string>(library.projectTypes[0].id);
  const [pendingDelete, setPendingDelete] = useState<LibraryBlock | null>(null);
  const rateIndex = buildRateIndex(ratecard);
  const blocks = library.blocks.filter((b) => b.projectType === type);
  const currentType = library.projectTypes.find((p) => p.id === type);
  const typeName = currentType?.name ?? "";
  // Уже заведённые семейства подсказываем при вводе: связь идёт по названию, и
  // «HR-гайдбук» с «HR гайдбук» оказались бы разными семьями.
  const families = [...new Set(library.blocks.map((b) => b.family).filter(Boolean))] as string[];

  function save(next: BlocksLibrary, failure: string) {
    void updateLibrary(next).catch(() => toast.error(failure));
  }

  function patchBlock(blockId: string, fn: (b: LibraryBlock) => LibraryBlock) {
    save(
      { ...library, blocks: library.blocks.map((b) => (b.id === blockId ? fn(b) : b)) },
      "Не удалось сохранить блок",
    );
  }

  /**
   * Новый блок — пустой, но сразу в справочнике.
   *
   * Диалога «как назовём» нет намеренно: он спрашивает то, что человек и так
   * собирается вписать, а пустая карточка сверху с курсором в названии
   * говорит то же самое и не отнимает шага.
   */
  function addBlock() {
    const id = `block-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
    const fresh: LibraryBlock = {
      id,
      name: "",
      goal: "",
      result: "",
      projectType: type,
      lines: [],
    };
    save(
      {
        ...library,
        blocks: [fresh, ...library.blocks],
        // Тип помнит состав отдельным списком — иначе счётчик на вкладке
        // разойдётся с тем, что видно под ней. И сразу в этап: без этого блок
        // ложился бы в смете в первый попавшийся.
        projectTypes: library.projectTypes.map((p) =>
          p.id === type ? placeInStage({ ...p, blockIds: [id, ...p.blockIds] }, id) : p,
        ),
      },
      "Не удалось добавить блок",
    );
  }

  function moveToStage(blockId: string, stageIndex: number) {
    save(
      {
        ...library,
        projectTypes: library.projectTypes.map((p) =>
          p.id === type ? placeInStage(p, blockId, stageIndex) : p,
        ),
      },
      "Не удалось переставить блок в этап",
    );
  }

  function removeBlock(block: LibraryBlock) {
    setPendingDelete(null);
    save(
      {
        ...library,
        // Снятый блок запоминаем: пополнение из зерна умеет только добавлять
        // и «удалённый» от «нового» не отличает — без этого списка блок
        // возвращался бы при каждой выкатке с новой версией зерна.
        removedBlocks: [...new Set([...(library.removedBlocks ?? []), block.id])],
        blocks: library.blocks.filter((b) => b.id !== block.id),
        projectTypes: library.projectTypes.map((p) => ({
          ...p,
          blockIds: p.blockIds.filter((id) => id !== block.id),
          stages: p.stages.map((s) => ({ ...s, blocks: s.blocks.filter((id) => id !== block.id) })),
        })),
      },
      "Не удалось убрать блок",
    );
  }

  /**
   * Свой тип проекта.
   *
   * Заводится пустым и сразу выбранным: имя вписывается в карточке под
   * вкладками. Юрлицо берём первое из справочника — у Braining это ООО с НДС,
   * самый частый случай, и в самой смете оно всё равно переключается.
   *
   * Этапов у нового типа нет: структура таблицы появится, когда её захотят.
   * Блок без этапа ложится в первый — так же ведут себя типы из архива, где
   * этапы размечены только у «Бренда работодателя».
   */
  function addProjectType() {
    const id = `type-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 5)}`;
    save(
      {
        ...library,
        projectTypes: [
          ...library.projectTypes,
          {
            id,
            name: "",
            taxScheme: library.taxSchemes[0]?.id ?? "ooo_nds22",
            stages: [],
            blockIds: [],
          },
        ],
      },
      "Не удалось добавить тип проекта",
    );
    setType(id);
  }

  function renameProjectType(typeId: string, name: string) {
    const next = name.trim();
    if (!next) return;
    save(
      {
        ...library,
        projectTypes: library.projectTypes.map((p) => (p.id === typeId ? { ...p, name: next } : p)),
      },
      "Не удалось переименовать тип проекта",
    );
  }

  function patchLine(blockId: string, index: number, fn: (l: LibraryLine) => LibraryLine) {
    patchBlock(blockId, (b) => ({
      ...b,
      lines: b.lines.map((l, i) => (i === index ? fn(l) : l)),
    }));
  }

  /**
   * Новая строка — час по рейткарте.
   *
   * Это самый частый случай: по архиву час занимает 61% строк. Услугу целиком
   * заводят, переключив тип цены, — тогда цена вводится прямо здесь, а
   * рейткарта не при чём. Аня об этом просила отдельно: «не только
   * специалисты, там может же быть и услуга целиком зашита, типа разработка
   * ролика».
   */
  function addLine(blockId: string) {
    patchBlock(blockId, (b) => ({
      ...b,
      lines: [...b.lines, { name: "", unit: "час", qty: 1, priceSource: "ratecard" }],
    }));
  }

  function removeLine(blockId: string, index: number) {
    patchBlock(blockId, (b) => ({ ...b, lines: b.lines.filter((_, i) => i !== index) }));
  }

  return (
    <div className="space-y-5">
      <AlertDialog open={Boolean(pendingDelete)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Убрать «{pendingDelete?.name || "блок без названия"}»?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Блок исчезнет из справочника и больше не будет предлагаться в новых калькуляторах. Уже
              собранные сметы не изменятся: блок в них лежит целиком, со строками и ставками.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingDelete(null)}>Оставить</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={() => pendingDelete && removeBlock(pendingDelete)}
            >
              Убрать
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Блоки работ</h1>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
          {library.blocks.length} блоков. Название, состав и цены правятся на месте, блок можно
          добавить или убрать. Собранные калькуляторы от этого не меняются — блок в них записан
          целиком.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {library.projectTypes.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setType(p.id)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-sm transition-colors",
              type === p.id
                ? "border-primary bg-primary text-primary-foreground"
                : "text-muted-foreground hover:border-input hover:text-foreground",
            )}
          >
            {p.name}
            <span className="ml-1.5 opacity-60">{p.blockIds.length}</span>
          </button>
        ))}

        {/* Свой тип проекта. Аня на демо: «здесь не хватает типов просто. Я
            себе в голове придумала, что я сейчас считаю смету на мерч, но
            здесь не она». Список от Braining ждать не нужно — заведут сами. */}
        <button
          type="button"
          onClick={addProjectType}
          className="rounded-full border border-dashed px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:border-input hover:text-foreground"
          title="Добавить тип проекта"
        >
          <Plus className="mr-1 inline size-3.5" />
          тип проекта
        </button>
      </div>

      {/* Название нового типа правится там же, где он выбран: заводить его
          через диалог значило бы спрашивать имя дважды. */}
      {library.projectTypes.some((p) => p.id === type && !p.name) && (
        <Card>
          <CardContent className="flex flex-wrap items-center gap-3 py-3">
            <span className="text-sm font-medium">Новый тип проекта</span>
            <TextField
              value=""
              placeholder="например, мерч"
              className="w-64"
              onCommit={(v) => renameProjectType(type, v)}
            />
            <span className="text-xs text-muted-foreground">
              Юрлицо — {library.taxSchemes[0]?.name}; поменять можно в самой смете.
            </span>
          </CardContent>
        </Card>
      )}

      <PlanFields projectType={type} projectTypeName={typeName} />

      {canLearn && <BlockDraftCard projectType={type} />}

      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">
          {counted(blocks.length, "блок", "блока", "блоков")} в этом типе проекта
        </span>
        <Button size="sm" variant="outline" onClick={addBlock}>
          <Plus className="size-4" />
          Добавить блок
        </Button>
      </div>

      <div className="grid gap-5">
        {blocks.map((block) => (
          <BlockCard
            key={block.id}
            block={block}
            stages={currentType?.stages.map((s) => s.name) ?? []}
            stageIndex={currentType ? stageIndexOf(currentType, block.id) : -1}
            onStage={(i) => moveToStage(block.id, i)}
            rateIndex={rateIndex}
            roles={flatRoles(ratecard)}
            families={families}
            siblings={library.blocks.filter(
              (b) =>
                b.id !== block.id &&
                b.family &&
                block.family &&
                b.family.trim().toLowerCase() === block.family.trim().toLowerCase(),
            )}
            onPatch={(fn) => patchBlock(block.id, fn)}
            onPatchLine={(i, fn) => patchLine(block.id, i, fn)}
            onAddLine={() => addLine(block.id)}
            onRemoveLine={(i) => removeLine(block.id, i)}
            onRemove={() => setPendingDelete(block)}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Карточка блока: слева смысл, справа состав.
 *
 * Всё правится на месте, без режима редактирования. Отдельная кнопка
 * «изменить» здесь была бы лишним шагом: справочник и существует затем, чтобы
 * его дописывали — «мы постепенно будем стандартизировать вот эти блоки
 * работ».
 */
function BlockCard({
  block,
  stages,
  stageIndex,
  onStage,
  rateIndex,
  roles,
  families,
  siblings,
  onPatch,
  onPatchLine,
  onAddLine,
  onRemoveLine,
  onRemove,
}: {
  block: LibraryBlock;
  /** этапы типа проекта; пусто или один — выбирать нечего */
  stages: string[];
  stageIndex: number;
  onStage: (index: number) => void;
  rateIndex: ReturnType<typeof buildRateIndex>;
  roles: RatecardRole[];
  families: string[];
  /** блоки того же семейства — по ним подсказка говорит правду, а не теорию */
  siblings: LibraryBlock[];
  onPatch: (fn: (b: LibraryBlock) => LibraryBlock) => void;
  onPatchLine: (index: number, fn: (l: LibraryLine) => LibraryLine) => void;
  onAddLine: () => void;
  onRemoveLine: (index: number) => void;
  onRemove: () => void;
}) {
  const total = block.lines.reduce(
    (s, l) => s + resolveRate({ ...l, id: "" }, rateIndex).rate * l.qty,
    0,
  );

  /*
   * Только что заведённый блок: ни имени, ни строк. Жалоба Вики №9 от 21.09
   * — «нужно назваие блока»: она нажала «Добавить блок», карточка появилась
   * сверху с суммой 0 ₽ и без заголовка, курсор при этом остался там, где
   * был. Блок так и уехал в справочник безымянным. Теперь курсор сразу в
   * названии, а пустое имя названо вслух — чтобы не читалось как пропавший
   * заголовок.
   */
  const fresh = !block.name.trim() && block.lines.length === 0;

  return (
    <Card>
      <CardContent className="grid gap-5 py-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.15fr)]">
        <div className="min-w-0 space-y-2">
          {/*
            Перенос, а не одна строка. Жалоба Вики №7 от 21.09: «наезжает
            текст на рейт карту» — на 1364 сумма с припиской «по архиву»,
            селект этапа и корзина не помещались в левую колонку и вылезали
            на правую, поверх строк ролей.
          */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <TextField
              value={block.name}
              placeholder="название блока"
              autoFocus={fresh}
              // Название — во всю ширину колонки: оно длинное («Коммуникационная
              // стратегия для внешних каналов коммуникации»), и делить строку с
              // суммой и этапом значило бы резать его до неузнаваемости.
              className="basis-full font-medium"
              onCommit={(v) => onPatch((b) => ({ ...b, name: v }))}
            />
            {!block.name.trim() && (
              <span className="shrink-0 text-xs text-warning-ink">впишите название</span>
            )}
            {block.newAt && (
              <NewMark
                className="shrink-0"
                title="Блок собрали в сметах, справочник его выучил"
                onClear={() => onPatch(({ newAt: _n, ...rest }) => rest)}
              />
            )}
            <span
              className="shrink-0 whitespace-nowrap text-sm font-semibold tabular-nums"
              title={
                block.stats
                  ? `По архиву блок стоил около ${rub(block.stats.medianNet)} — ` +
                    `${counted(block.stats.count, "смета", "сметы", "смет")}`
                  : undefined
              }
            >
              {rub(total)}
              {block.stats && (
                <span className="ml-1.5 font-normal text-muted-foreground">
                  · по архиву ≈ {rub(block.stats.medianNet)}
                </span>
              )}
            </span>
            {stages.length > 1 && (
              <Select
                value={stageIndex >= 0 ? String(stageIndex) : "none"}
                onValueChange={(v) => onStage(Number(v))}
              >
                <SelectTrigger
                  className="h-7 w-auto max-w-44 shrink-0 text-xs"
                  aria-label="Этап"
                  title="В каком этапе блок стоит в смете"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {stageIndex < 0 && (
                    <SelectItem value="none" disabled>
                      без этапа
                    </SelectItem>
                  )}
                  {stages.map((name, i) => (
                    <SelectItem key={i} value={String(i)}>
                      {name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <button
              type="button"
              onClick={onRemove}
              className="shrink-0 text-muted-foreground hover:text-destructive"
              aria-label={`Убрать блок ${block.name || "без названия"}`}
              title="Убрать блок из справочника"
            >
              <Trash2 className="size-4" />
            </button>
          </div>

          <TextField
            value={block.goal}
            placeholder="цель и подход — что именно делаем"
            multiline
            className="text-xs leading-relaxed text-muted-foreground"
            onCommit={(v) => onPatch((b) => ({ ...b, goal: v }))}
          />
          <TextField
            value={block.result}
            placeholder="результат — что получит клиент"
            multiline
            className="text-xs leading-relaxed text-muted-foreground"
            onCommit={(v) => onPatch((b) => ({ ...b, result: v }))}
          />

          {/*
            Семейство связывает варианты одного блока — гайдбук мини и макси.
            Связь по названию, потому что заводит их человек: вписать одно и то
            же слово в двух блоках проще, чем объяснять, что такое «связать».
            В смете такой блок меняется на соседний одним кликом.
          */}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground">Семейство</span>
            <TextField
              value={block.family ?? ""}
              placeholder="например, HR-гайдбук"
              list="library-families"
              className="w-48 text-xs"
              onCommit={(v) => onPatch((b) => ({ ...b, family: v || undefined }))}
            />
            <TextField
              value={block.variant ?? ""}
              placeholder="мини, макси"
              className="w-28 text-xs"
              onCommit={(v) => onPatch((b) => ({ ...b, variant: v || undefined }))}
            />
            <datalist id="library-families">
              {families.map((f) => (
                <option key={f} value={f} />
              ))}
            </datalist>
          </div>

          {/*
            Подсказка живая, по состоянию: поле «Семейство» без объяснения
            сбивало с толку — что это, задано ли уже где-то, на что влияет.
          */}
          <p className="text-[11px] leading-relaxed text-muted-foreground">
            {siblings.length > 0 ? (
              <>
                Варианты этого семейства:{" "}
                {[block, ...siblings].map((b) => b.variant?.trim() || b.name).join(" · ")}. В смете
                блок меняется на соседний вариант одним кликом — состав и цена берутся у выбранного.
              </>
            ) : block.family?.trim() ? (
              <>
                Пока единственный вариант в семействе «{block.family.trim()}». Впишите то же
                семейство во второй блок — и в смете они будут меняться одним кликом, как гайдбук
                мини и макси.
              </>
            ) : (
              <>
                Семейство связывает блоки-варианты одного объёма — «HR-гайдбук» мини и макси. Одно
                имя семейства в двух блоках, и в смете вариант меняется одним кликом.
              </>
            )}
          </p>
        </div>

        <div className="h-fit">
          {/*
            table-fixed обязателен. В auto-раскладке у инпутов количества и
            цены браузерная минимальная ширина (~20 символов) перевешивала
            w-16/w-28 на ячейках: колонки цифр разъезжались, а имя роли —
            единственная сжимаемая колонка — схлопывалось до одной буквы.
          */}
          <table className="w-full table-fixed text-xs">
            <tbody>
              {block.lines.map((l, i) => (
                <LineRow
                  key={i}
                  line={l}
                  roles={roles}
                  sum={resolveRate({ ...l, id: "" }, rateIndex).rate * l.qty}
                  onPatch={(fn) => onPatchLine(i, fn)}
                  onRemove={() => onRemoveLine(i)}
                />
              ))}
            </tbody>
          </table>
          <button
            type="button"
            onClick={onAddLine}
            className="mt-2 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <Plus className="size-3.5" />
            строка
          </button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Строка блока: специалист по рейткарте или услуга со своей ценой.
 *
 * Тип цены переключается кликом по бейджу — это и есть ответ на «не только
 * специалисты, там может же быть и услуга целиком зашита». У услуги цена
 * вводится здесь же, у специалиста берётся из прайса по названию.
 */
function LineRow({
  line,
  roles,
  sum,
  onPatch,
  onRemove,
}: {
  line: LibraryLine;
  roles: RatecardRole[];
  sum: number;
  onPatch: (fn: (l: LibraryLine) => LibraryLine) => void;
  onRemove: () => void;
}) {
  const direct = line.priceSource === "direct";

  return (
    <tr className="border-b last:border-0">
      <td className="py-1.5 pr-2">
        <div className="flex items-center gap-1.5">
          <TextField
            value={line.name}
            placeholder={direct ? "название услуги" : "специалист"}
            list={direct ? undefined : "library-roles"}
            className="min-w-0 flex-1"
            onCommit={(v) => onPatch((l) => ({ ...l, name: v }))}
          />
          <button
            type="button"
            onClick={() =>
              onPatch((l) =>
                direct
                  ? { ...l, priceSource: "ratecard", unitPrice: undefined }
                  : // Своя цена нужна сразу: строка без неё считалась бы нулём
                    // и молча обесценивала блок.
                    { ...l, priceSource: "direct", unitPrice: l.unitPrice ?? 0 },
              )
            }
            className={cn(
              "shrink-0 rounded border px-1.5 py-0.5 text-[10px] transition-colors",
              direct
                ? "border-primary/40 bg-primary/5 text-foreground"
                : "text-muted-foreground hover:text-foreground",
            )}
            title={
              direct
                ? "Услуга целиком: цена задана здесь. Нажмите, чтобы считать по рейткарте"
                : "Специалист: цена из рейткарты по названию. Нажмите, чтобы задать свою"
            }
          >
            {direct ? "своя цена" : "прайс"}
          </button>
        </div>
        <datalist id="library-roles">
          {roles.map((r) => (
            <option key={r.id} value={r.name} />
          ))}
        </datalist>
      </td>

      <td className="w-12 py-1.5">
        <NumField
          value={line.qty}
          className="w-full text-right"
          onCommit={(v) => onPatch((l) => ({ ...l, qty: v }))}
        />
      </td>
      <td className="w-16 py-1.5 pl-1">
        <TextField
          value={line.unit}
          list="library-units"
          className="w-full"
          onCommit={(v) => onPatch((l) => ({ ...l, unit: v }))}
        />
        <datalist id="library-units">
          {UNITS.map((u) => (
            <option key={u} value={u} />
          ))}
        </datalist>
      </td>

      <td className="w-24 py-1.5 pl-1 text-right tabular-nums">
        {direct ? (
          <NumField
            value={line.unitPrice ?? 0}
            className="w-full text-right"
            onCommit={(v) => onPatch((l) => ({ ...l, unitPrice: v }))}
          />
        ) : (
          <span title="Цена из рейткарты по названию специалиста">{rub(sum)}</span>
        )}
      </td>
      <td className="w-6 py-1.5 pl-1 text-right">
        <button
          type="button"
          onClick={onRemove}
          className="text-muted-foreground hover:text-destructive"
          aria-label={`Убрать строку ${line.name || "без названия"}`}
        >
          <X className="size-3.5" />
        </button>
      </td>
    </tr>
  );
}

/**
 * Плановая рентабельность: по подотделам и по выбранному типу проекта.
 *
 * Стоит здесь, а не в отдельном разделе, потому что типы проектов выбираются
 * ровно на этом экране. Цифры вносит агентство, без релиза: Braining своих
 * пока не назвали ни по одной строке.
 *
 * Два ряда, потому что и planка живёт на двух уровнях. Аня: «Comm делится на
 * brands, digital и media, и у них разная рента», а тип проекта уточняет —
 * он работает там, где подотдел у сметы не проставлен.
 *
 * Пустое поле значит «своей цифры нет»: тогда работает то, что ниже по
 * цепочке, вплоть до общего ориентира. Это честнее выдуманного числа.
 */
function PlanFields({ projectType, projectTypeName }: PlanFieldsProps) {
  const { settings, updateSettings, library, ready } = useEstimates();
  const plans = settings.planMargin;
  const fallback = DEFAULT_POLICY.planMarginDefault;

  /**
   * Сохранение с ответом на экране.
   *
   * Жалоба Вики №8 от 21.09: «не могу поменять плановую рнетабельность». Поле
   * писалось в базу молча: цифра оставалась в поле, но ничем не отличалась от
   * серого ориентира рядом, и понять, приняли её или нет, было нельзя. Тост
   * отвечает на единственный вопрос, который здесь задают.
   */
  function save(next: typeof plans, said: string) {
    void updateSettings({ ...settings, planMargin: next })
      .then(() => toast.success(said))
      .catch(() => {
        // Стор откатил значение и показал ошибку — здесь добавить нечего.
      });
  }

  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="w-40 shrink-0 text-sm font-medium">Плановая рентабельность</span>
          {library.subdivisions.map((sub) => (
            <PlanInput
              key={sub}
              label={sub}
              disabled={!ready}
              value={plans.bySubdivision[sub]}
              placeholder={fallback}
              onCommit={(v) => {
                const bySubdivision = { ...plans.bySubdivision };
                if (v === undefined) delete bySubdivision[sub];
                else bySubdivision[sub] = v;
                save(
                  { bySubdivision, byType: plans.byType },
                  v === undefined
                    ? `${sub}: своей цифры нет — считаем по общему ориентиру`
                    : `${sub}: план ${pct(v, 0)}`,
                );
              }}
            />
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="w-40 shrink-0 text-sm text-muted-foreground">
            Если считаем иначе для «{projectTypeName}»
          </span>
          <PlanInput
            label="этот тип"
            disabled={!ready}
            value={plans.byType[projectType]}
            placeholder={fallback}
            onCommit={(v) => {
              const byType = { ...plans.byType };
              if (v === undefined) delete byType[projectType];
              else byType[projectType] = v;
              save(
                { bySubdivision: plans.bySubdivision, byType },
                v === undefined
                  ? `«${projectTypeName}»: своей цифры нет — считаем по подотделу`
                  : `«${projectTypeName}»: план ${pct(v, 0)}`,
              );
            }}
          />
        </div>

        <p className="text-xs text-muted-foreground">
          Прочерк — своей цифры нет: считаем по общему ориентиру
          {typeof fallback === "number" ? ` ${pct(fallback, 0)}` : ""}. Впишите своё число и нажмите
          Enter. У сметы можно поставить свою цель — она перекроет подотдел и тип, а у блока внутри
          сметы свою: она главнее всех.
          {settings.version &&
            ` Правили ${new Date(settings.version).toLocaleDateString("ru-RU")}.`}{" "}
          Суммы собранных калькуляторов от правки не меняются: план — ориентир, а не цена.
        </p>
      </CardContent>
    </Card>
  );
}

interface PlanFieldsProps {
  projectType: string;
  projectTypeName: string;
}

/** Поле процента с подписью: коммит по Enter и уходу из поля, как в рейткарте. */
function PlanInput({
  label,
  value,
  placeholder,
  disabled,
  onCommit,
}: {
  label: string;
  value: number | undefined;
  placeholder: number | undefined;
  disabled?: boolean;
  onCommit: (value: number | undefined) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (typeof value === "number" ? String(Math.round(value * 100)) : "");

  function commit() {
    if (draft === null) return;
    const parsed = parseInt10(draft);
    setDraft(null);

    // Пустое поле или ноль — не «план 0%», а отказ от собственной цифры.
    const next = parsed > 0 && parsed < 100 ? parsed / 100 : undefined;
    if (next !== value) onCommit(next);
  }

  return (
    <label className="flex items-center gap-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <Input
        inputMode="numeric"
        disabled={disabled}
        /*
          Прочерк, а не серое «50». Жалоба Вики №8 от 21.09: «не могу поменять
          плановую рнетабельность». Подсказка числом выглядела как заполненное
          поле, которое почему-то не стирается, — а поле было пустым и ждало
          ввода. Сколько получится, если оставить пусто, сказано словами под
          карточкой; здесь важнее, что цифры своей нет.
        */
        placeholder={disabled ? "…" : "—"}
        className="h-8 w-16 text-right tabular-nums"
        value={shown}
        onFocus={(e) => {
          setDraft(e.currentTarget.value);
          e.currentTarget.select();
        }}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.currentTarget.blur();
          if (e.key === "Escape") {
            setDraft(null);
            e.currentTarget.blur();
          }
        }}
      />
      <span className="text-xs text-muted-foreground">%</span>
    </label>
  );
}

/**
 * Текст, который правится на месте: коммит по уходу из поля и по Enter.
 *
 * Escape возвращает как было — это дешёвая страховка для справочника, где
 * правка сразу уезжает в базу.
 */
function TextField({
  value,
  placeholder,
  className,
  list,
  multiline,
  autoFocus,
  onCommit,
}: {
  value: string;
  placeholder?: string;
  className?: string;
  list?: string;
  multiline?: boolean;
  /** поставить курсор сразу — для только что заведённого блока */
  autoFocus?: boolean;
  onCommit: (v: string) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  // Многострочное поле в покое — текст, а не textarea: обрезается с «…», как
  // просили глаза, и не тратит место на пустые рамки. Редактор открывается
  // кликом.
  const [editing, setEditing] = useState(false);
  const shown = draft ?? value;

  function commit() {
    if (draft === null) return;
    const next = draft.trim();
    setDraft(null);
    if (next !== value) onCommit(next);
  }

  const common = {
    value: shown,
    placeholder,
    onChange: (e: { target: { value: string } }) => setDraft(e.target.value),
    onBlur: commit,
  };

  if (multiline) {
    if (!editing) {
      return (
        <div
          role="button"
          tabIndex={0}
          onClick={() => setEditing(true)}
          onKeyDown={(e) => e.key === "Enter" && setEditing(true)}
          // line-clamp обрезает длинный текст многоточием в конце видимой
          // части — раньше он резался по нижнему краю на полуслове, и было
          // непонятно, обрыв это или конец.
          className={cn(
            "line-clamp-6 cursor-text whitespace-pre-wrap rounded-md border border-transparent px-2 py-1.5 hover:border-input",
            !value && "text-muted-foreground/60",
            className,
          )}
          title="Нажмите, чтобы раскрыть и поправить"
        >
          {value || placeholder}
        </div>
      );
    }

    return (
      <Textarea
        {...common}
        autoFocus
        rows={2}
        // В редакторе поле раскрыто целиком и растёт под текст: правят его
        // глазами, не через щель со скроллом.
        className={cn("field-sizing-content min-h-0 resize-none border-input px-2", className)}
        onFocus={(e) => {
          const end = e.currentTarget.value.length;
          e.currentTarget.setSelectionRange(end, end);
        }}
        onBlur={() => {
          commit();
          setEditing(false);
        }}
        onKeyDown={(e) => {
          // Enter в многострочном поле — перенос строки, а не сохранение:
          // в цели и результате абзацы нужны.
          if (e.key === "Escape") {
            setDraft(null);
            setEditing(false);
          }
        }}
      />
    );
  }

  return (
    <Input
      {...common}
      list={list}
      autoFocus={autoFocus}
      className={cn("h-8 border-transparent px-2 hover:border-input", className)}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setDraft(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** Число, которое правится на месте. Пустое и ноль равнозначны нулю. */
function NumField({
  value,
  className,
  onCommit,
}: {
  value: number;
  className?: string;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? (value ? int(value) : "");

  function commit() {
    if (draft === null) return;
    const next = parseInt10(draft);
    setDraft(null);
    if (next !== value) onCommit(next);
  }

  return (
    <Input
      inputMode="numeric"
      value={shown}
      placeholder="0"
      className={cn("h-8 border-transparent px-2 tabular-nums hover:border-input", className)}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setDraft(null);
          e.currentTarget.blur();
        }
      }}
    />
  );
}
